/**
 * Persona ↔ theme 對應表 + onboarding picker 順序。
 *
 * 對應規則（spec §3）：
 *   - luna  → morandi（溫柔陪伴）
 *   - orion → mystic （神秘權威）
 *   - sage  → minimal（靈性分析）
 *   - silhouette 是 4th 選項，本身不是真 persona，按下去會跑 3 問校準。
 */

export type Persona = 'luna' | 'orion' | 'sage';
export type Theme = 'morandi' | 'mystic' | 'minimal';
export type PickerOption = Persona | 'silhouette';

export const PERSONAS: Persona[] = ['luna', 'orion', 'sage'];

export const PICKER_OPTIONS: PickerOption[] = ['luna', 'orion', 'sage', 'silhouette'];

export const PERSONA_TO_THEME: Record<Persona, Theme> = {
  luna: 'morandi',
  orion: 'mystic',
  sage: 'minimal',
};

export function applyPersonaTheme(persona: Persona): void {
  const theme = PERSONA_TO_THEME[persona];
  document.documentElement.setAttribute('data-theme', theme);
}

/** 給 silhouette 選項用的中性視覺漸層（沒對應真 theme）。 */
export const SILHOUETTE_SWATCH = 'linear-gradient(135deg,#1a1a1a 0%,#3a3a3a 60%,#5a5a5a 100%)';

/** Avatar fallback 漸層（圖片載失敗時顯示）。 */
export const AVATAR_SWATCH: Record<PickerOption, string> = {
  luna: 'linear-gradient(135deg,#f5f1eb 0%,#d8cfc0 50%,#b8a89a 100%)',
  orion: 'linear-gradient(135deg,#0a0a0f 0%,#4a3a8a 60%,#d4af37 100%)',
  sage: 'linear-gradient(135deg,#ffffff 0%,#e5e5e5 50%,#111111 100%)',
  silhouette: SILHOUETTE_SWATCH,
};

export function isPersona(value: unknown): value is Persona {
  return value === 'luna' || value === 'orion' || value === 'sage';
}

export function isTheme(value: unknown): value is Theme {
  return value === 'morandi' || value === 'mystic' || value === 'minimal';
}

export function lockedPersona(): Persona | null {
  const value = import.meta.env.VITE_LOCK_READER ?? import.meta.env.VITE_SKU_LOCK;
  return isPersona(value) ? value : null;
}

export function lockedTheme(): Theme | null {
  const value = import.meta.env.VITE_LOCK_THEME;
  return isTheme(value) ? value : null;
}

export function themeForPersona(persona: Persona): Theme {
  return lockedTheme() ?? PERSONA_TO_THEME[persona];
}
